import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { type AppointmentCategories } from 'features/Schedule/api/schedule.types'
import { type AppointmentColorType } from 'features/SettingsOld/interfaces/ISettings'
import { type StatusColorName } from 'features/SettingsOld/state/api/types'

export const getAppointmentColorInputFromEvent = (
  data: Pick<IPatientSchedule, 'id' | 'status' | 'category'>,
  schedules: IPatientSchedule[],
): { statusCode?: number; categoryCode?: number } => {
  const fromSchedules = schedules.find((s) => s.id === data.id)
  return {
    statusCode: data.status?.code ?? fromSchedules?.status?.code,
    categoryCode: data.category?.code ?? fromSchedules?.category?.code,
  }
}

export const getAppointmentColor = ({
  appointmentColor,
  categoryStatuses,
  statusColorName,
  statusCode,
  categoryCode,
}: {
  appointmentColor: AppointmentColorType
  categoryStatuses: AppointmentCategories[]
  statusColorName: StatusColorName[]
  statusCode?: number
  categoryCode?: number
}) => {
  const status = Array.isArray(statusColorName)
    ? statusColorName.find((s) => s.code === statusCode || Number(s.code) === Number(statusCode))
    : undefined
  const category = Array.isArray(categoryStatuses)
    ? categoryStatuses.find(
        (c) => c.code === categoryCode || Number(c.code) === Number(categoryCode),
      )
    : undefined

  if (appointmentColor === 'status') {
    return {
      color: status?.color,
      background: status?.background,
      borderColor: category?.color,
    }
  }
  return { color: category?.color, background: category?.background, borderColor: status?.color }
}

type ScheduleUpdateOverrides = {
  room: string | null | undefined
  start_time: string
  end_time: string
}

export const buildScheduleUpdatePayload = (
  event: IPatientSchedule,
  { room, start_time, end_time }: ScheduleUpdateOverrides,
) => ({
  date: event.date,
  room,
  start_time,
  end_time,
  temperature_bypass: event.temperature_bypass,
  consultation_staff: event.consultation_staff,
  procedure_code: !event.procedure?.isCustom ? event.procedure?.code : null,
  procedure_description: event.procedure?.description,
  category_type_code: event.category?.code,
  title: event.title,
  comments: event.comments,
  punctuality_type_code: Number(event.punctuality?.appointment?.code || 0),
  punctuality_minutes: event.punctuality?.appointment?.minutes || 0,
  status_type_code: event.status?.code,
  patient: {
    id: event.patient?.id,
    first_name: event.patient?.first_name,
    last_name: event.patient?.last_name,
    phone: event.patient?.phone,
    language_type_code: event.patient?.language_type_code,
  },
})

export const snapToFiveMinutes = (date: Date): Date => {
  const snappedDate = new Date(date)
  const minutes = snappedDate.getMinutes()
  const remainder = minutes % 5

  if (remainder === 0) {
    return snappedDate
  }
  const adjustment = remainder < 3 ? -remainder : 5 - remainder
  snappedDate.setMinutes(minutes + adjustment)
  snappedDate.setSeconds(0)
  snappedDate.setMilliseconds(0)

  return snappedDate
}
